/**
 * <sticky-atc> custom element
 *
 * - IntersectionObserver: shows the bar when the main ATC scrolls out of view
 * - Listens to the product form for variant changes; updates variant text + price live
 * - Falls back to MutationObserver on the main price element for indirect updates
 * - Clicks the real ATC button when the sticky button is clicked
 * - Mobile only: positions the Sticky ATC directly beneath Shopify's visible
 *   sticky header, or at the top of the viewport when the header is hidden
 */

class StickyATC extends HTMLElement {
  constructor() {
    super();

    this.targetId = this.dataset.target;
    this.sectionId = this.dataset.sectionId;

    this.targetButton = null;
    this.observer = null;
    this.priceObserver = null;
    this.headerObserver = null;

    this.variants = [];
    this.optionNames = [];

    this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');

    this.boundUpdateMobilePosition =
      this.updateMobilePosition.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => this.init(),
        { once: true }
      );
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.priceObserver) {
      this.priceObserver.disconnect();
    }

    if (this.headerObserver) {
      this.headerObserver.disconnect();
    }

    window.removeEventListener(
      'scroll',
      this.boundUpdateMobilePosition
    );

    window.removeEventListener(
      'resize',
      this.boundUpdateMobilePosition
    );

    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener(
          'change',
          this.boundUpdateMobilePosition
        );
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(
          this.boundUpdateMobilePosition
        );
      }
    }
  }

  init() {
    this.targetButton = document.getElementById(this.targetId);

    if (!this.targetButton) {
      this.remove();
      return;
    }

    this.parseVariants();
    this.cacheOptionNames();

    this.setupIntersectionObserver();
    this.setupVariantChangeListener();
    this.setupPriceMirror();
    this.bindStickyButton();

    /*
     * The old mobile ATC supported collapse/expand.
     * The new mobile layout is a compact top bar, so remove
     * any previously stored collapsed state.
     */
    this.clearLegacyCollapsedState();

    /*
     * Track Shopify's sticky header on mobile.
     */
    this.setupMobileHeaderTracking();
  }

  clearLegacyCollapsedState() {
    this.classList.remove('sticky-atc--collapsed');

    try {
      sessionStorage.removeItem('sticky-atc-collapsed');
    } catch (e) {
      // sessionStorage may be unavailable in some privacy modes.
    }
  }

  parseVariants() {
    const dataEl = this.querySelector(
      'script[data-sticky-variants]'
    );

    if (!dataEl) return;

    try {
      this.variants = JSON.parse(
        dataEl.textContent.trim()
      );
    } catch (e) {
      this.variants = [];
    }
  }

  cacheOptionNames() {
    const productInfo = document.getElementById(
      'MainProduct-' + this.sectionId
    );

    if (!productInfo) return;

    const legends = productInfo.querySelectorAll(
      '.product-form__input legend, .product-form__input .form__label'
    );

    this.optionNames = Array.from(legends).map((el) => {
      const text = el.textContent.trim();

      return text.split(':')[0].trim();
    });
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          /*
           * Show only when the real product ATC has completely
           * scrolled above the viewport.
           *
           * If the real button is merely below the fold,
           * isIntersecting is also false, so bottom <= 0 is
           * necessary to distinguish the two situations.
           */
          const scrolledPast =
            !entry.isIntersecting &&
            entry.boundingClientRect.bottom <= 0;

          if (scrolledPast) {
            this.show();
          } else {
            this.hide();
          }
        });
      },
      {
        threshold: 0
      }
    );

    this.observer.observe(this.targetButton);
  }

  setupVariantChangeListener() {
    const productInfo = document.getElementById(
      'MainProduct-' + this.sectionId
    );

    if (!productInfo) return;

    productInfo.addEventListener('change', () => {
      /*
       * Give Shopify a moment to update the hidden
       * variant ID in the product form.
       */
      setTimeout(
        () => this.syncFromForm(),
        50
      );
    });

    productInfo.addEventListener('click', (e) => {
      if (
        e.target.closest(
          'input[type="radio"], .swatch, [data-option-value]'
        )
      ) {
        setTimeout(
          () => this.syncFromForm(),
          100
        );
      }
    });
  }

  syncFromForm() {
    const productInfo = document.getElementById(
      'MainProduct-' + this.sectionId
    );

    if (!productInfo) return;

    const variantIdInput =
      productInfo.querySelector('input[name="id"]');

    if (
      !variantIdInput ||
      !variantIdInput.value
    ) {
      return;
    }

    const variantId = parseInt(
      variantIdInput.value,
      10
    );

    const variant = this.variants.find(
      (v) => v.id === variantId
    );

    if (!variant) return;

    this.updateVariantInfo(variant);
  }

  updateVariantInfo(variant) {
    const variantEl = this.querySelector(
      '[data-sticky-variant]'
    );

    if (
      variantEl &&
      variant.options
    ) {
      const text = variant.options
        .map((value, idx) => {
          const name =
            this.optionNames[idx] ||
            ('Option ' + (idx + 1));

          return name + ': ' + value;
        })
        .join(' | ');

      variantEl.textContent = text;
    }
  }

  setupPriceMirror() {
    const mainPriceContainer =
      document.getElementById(
        'price-' + this.sectionId
      );

    const stickyPriceEl =
      this.querySelector(
        '[data-sticky-price]'
      );

    if (
      !mainPriceContainer ||
      !stickyPriceEl
    ) {
      return;
    }

    const updatePrice = () => {
      const saleEl =
        mainPriceContainer.querySelector(
          '.price-item--sale.price-item--last'
        );

      const regularEl =
        mainPriceContainer.querySelector(
          '.price-item--regular'
        );

      const compareEl =
        mainPriceContainer.querySelector(
          '.price__sale .price-item--regular'
        );

      let current;
      let compare;

      if (
        saleEl &&
        saleEl.textContent.trim()
      ) {
        current = saleEl.textContent.trim();

        if (compareEl) {
          compare =
            compareEl.textContent.trim();
        }
      } else if (regularEl) {
        current =
          regularEl.textContent.trim();
      }

      if (!current) return;

      const currentEl =
        stickyPriceEl.querySelector(
          '.sticky-atc__price-current'
        );

      const compareElSticky =
        stickyPriceEl.querySelector(
          '.sticky-atc__price-compare'
        );

      if (currentEl) {
        currentEl.textContent = current;
      }

      if (
        compare &&
        compareElSticky
      ) {
        compareElSticky.textContent =
          compare;

        compareElSticky.style.display =
          '';
      } else if (compareElSticky) {
        compareElSticky.style.display =
          'none';
      }

      const pillPriceEl =
        this.querySelector(
          '[data-sticky-pill-price]'
        );

      if (pillPriceEl) {
        pillPriceEl.textContent =
          current;
      }
    };

    updatePrice();

    this.priceObserver =
      new MutationObserver(updatePrice);

    this.priceObserver.observe(
      mainPriceContainer,
      {
        childList: true,
        subtree: true,
        characterData: true
      }
    );
  }

  bindStickyButton() {
    const stickyBtn =
      this.querySelector(
        '[data-sticky-add-to-cart]'
      );

    if (!stickyBtn) return;

    stickyBtn.addEventListener(
      'click',
      (e) => {
        e.preventDefault();

        if (
          this.targetButton &&
          !this.targetButton.disabled
        ) {
          this.targetButton.click();
        }
      }
    );
  }

  /* =======================================================
     MOBILE HEADER TRACKING
     ======================================================= */

  setupMobileHeaderTracking() {
    /*
     * Dawn's header section wrapper.
     *
     * Your header.liquid adds/removes:
     *
     * shopify-section-header-hidden
     * shopify-section-header-sticky
     *
     * from this element as the customer scrolls.
     */
    this.headerSection =
      document.querySelector('.section-header');

    /*
     * Recalculate during scroll so the Sticky ATC follows
     * the header during its hide/reveal movement.
     */
    window.addEventListener(
      'scroll',
      this.boundUpdateMobilePosition,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      this.boundUpdateMobilePosition,
      { passive: true }
    );

    /*
     * Also listen for the mobile breakpoint changing.
     */
    if (this.mobileMediaQuery) {
      if (
        this.mobileMediaQuery.addEventListener
      ) {
        this.mobileMediaQuery.addEventListener(
          'change',
          this.boundUpdateMobilePosition
        );
      } else if (
        this.mobileMediaQuery.addListener
      ) {
        this.mobileMediaQuery.addListener(
          this.boundUpdateMobilePosition
        );
      }
    }

    /*
     * Watch the header's class changes.
     *
     * This catches Shopify adding/removing its
     * hidden/sticky/animate classes immediately.
     */
    if (this.headerSection) {
      this.headerObserver =
        new MutationObserver(() => {
          this.updateMobilePosition();

          /*
           * Shopify may animate the header after changing
           * its classes, so sample the position through
           * the next few animation frames as well.
           */
          requestAnimationFrame(() => {
            this.updateMobilePosition();

            requestAnimationFrame(() => {
              this.updateMobilePosition();
            });
          });
        });

      this.headerObserver.observe(
        this.headerSection,
        {
          attributes: true,
          attributeFilter: ['class']
        }
      );
    }

    this.updateMobilePosition();
  }

  updateMobilePosition() {
    /*
     * Desktop retains the original bottom-floating
     * Sticky ATC behavior.
     */
    if (
      !this.mobileMediaQuery.matches
    ) {
      this.style.removeProperty(
        '--sticky-atc-mobile-top'
      );

      return;
    }

    if (!this.headerSection) {
      this.style.setProperty(
        '--sticky-atc-mobile-top',
        '0px'
      );

      return;
    }

    const rect =
      this.headerSection.getBoundingClientRect();

    /*
     * rect.bottom gives us the actual bottom edge of
     * the header relative to the viewport.
     *
     * Header fully hidden:
     * rect.bottom <= 0 -> ATC top = 0
     *
     * Header visible:
     * rect.bottom > 0 -> ATC sits immediately beneath it
     */
    let headerBottom = rect.bottom;

    if (
      !Number.isFinite(headerBottom)
    ) {
      headerBottom = 0;
    }

    headerBottom = Math.max(
      0,
      headerBottom
    );

    /*
     * Never position the ATC below the viewport because
     * of an unexpected header measurement.
     */
    headerBottom = Math.min(
      headerBottom,
      window.innerHeight
    );

    this.style.setProperty(
      '--sticky-atc-mobile-top',
      `${Math.round(headerBottom)}px`
    );
  }

  show() {
    /*
     * Make sure the top position is correct immediately
     * before the ATC becomes visible.
     */
    this.updateMobilePosition();

    if (this.hasAttribute('hidden')) {
      this.removeAttribute('hidden');

      requestAnimationFrame(() => {
        this.classList.add(
          'sticky-atc--visible'
        );
      });
    }
  }

  hide() {
    this.classList.remove(
      'sticky-atc--visible'
    );

    setTimeout(() => {
      if (
        !this.classList.contains(
          'sticky-atc--visible'
        )
      ) {
        this.setAttribute(
          'hidden',
          ''
        );
      }
    }, 250);
  }
}

if (
  !customElements.get('sticky-atc')
) {
  customElements.define(
    'sticky-atc',
    StickyATC
  );
}